/**
 * RestartSourceSelector Component
 * RESTART 시 소스 프로젝트 → 모델 → 시뮬레이션 run을 선택하는 Cascading Select
 *
 * 시뮬레이션 이력은 BFF의 listSimulationHistoriesByProject API로 조회.
 *
 * 목록에 오르는 것은 **재시작 원본이 될 수 있는 실행뿐**이다(규칙은 restartSourceCoordinate 의
 * `restartSources` 한 곳): 완료됐고, 고른 모델이 참가했고, 재시작 파일과 짝(기준선 덱)을 남긴 실행.
 * rstplt 만 있는 실행은 그 상태가 어느 구성의 것인지 알 수 없어 편집을 오버라이드로 표현할 좌표계가
 * 없다(설계: docs/DESIGN-restart-baseline-pairing.md §4.3). 고른 뒤에야 «안 된다» 고 알리지 않는다.
 */

import React, { useEffect, useId, useRef, useState, useMemo, useCallback } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Chip,
} from '@mui/material';
import { runModeLabel, type Model, type Project, type RestartSource, type SimulationEntry } from '@/types/project';
import { useProjectStore } from '@/app/stores/projectStore';
import { useAsyncScope } from '@/shared/async/useAsyncScope';
import { listSimulationHistoriesByProject } from '@/app/services/pm/projectManagerService';
import { storageService } from '@/app/services/storage/storageService';
import { isCoupledRun, restartSourceEmptyText, restartSourceNotice, restartSources, runTaskIndexOf } from './restartSourceCoordinate';
import { formatDateTime } from '@/shared/dateTime';
import { runEntryName } from '@/shared/runName';
import { runStatusLabel, runStatusTone } from '@/shared/ui/runStatusView';
import { RUN_HISTORY_NOTICE } from '@/shared/ui/runHistoryNoticeView';

interface RestartSourceSelectorProps {
  value?: RestartSource;
  onChange: (source: RestartSource) => void;
}

const RestartSourceSelector: React.FC<RestartSourceSelectorProps> = ({ value, onChange }) => {
  const { projects, fetchProjects, fetchProjectById } = useProjectStore();
  // 목록 항목은 본문 없는 요약 — 선택한 프로젝트의 모델 목록은 그때 본문을 받아 만든다
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(false);
  // 실행 후보 — null 은 «아직 모른다»(조회 전·조회 중)이고 빈 목록은 «조회했더니 없다»다. 둘을 가르지
  // 않으면 조회가 끝나기 전 한 프레임 동안 선택기가 열리고 빈 문구가 보인다.
  const [simulationRuns, setSimulationRuns] = useState<SimulationEntry[] | null>(null);
  // 조회에 실패한 사유 — 빈 목록으로 꾸미지 않는다(«완료된 해석이 없다» 는 조회가 된 뒤에만 할 수 있는 말)
  const [runsError, setRunsError] = useState<string | null>(null);
  // 모델이 바뀐 뒤 도착한 이전 모델의 응답을 버린다
  const runsFetchSeq = useRef(0);
  // 프로젝트 본문을 받지 못한 사유 — 받지 못했으면 모델·실행을 알 수 없다(기다리는 것으로 꾸미지 않는다)
  const [projectError, setProjectError] = useState<string | null>(null);
  // 고른 실행에 대한 안내(커플링 참가자의 단독 재시작) — 규칙은 restartSourceNotice 한 곳
  const selectedRun = simulationRuns?.find((r) => r.id === value?.simulationId);
  const selectedNotice = selectedRun ? restartSourceNotice(selectedRun) : null;
  // 라벨을 선택기에 묶는다 — 보조 기술이 «Source Project» 등을 이름으로 읽고, 시험도 그 이름으로 찾는다
  const uid = useId();
  const labelIds = { project: `${uid}-project`, model: `${uid}-model`, run: `${uid}-run` };

  useEffect(() => {
    if (projects.length === 0) {
      setLoading(true);
      fetchProjects().finally(() => setLoading(false));
    }
  }, [projects.length, fetchProjects]);

  // MARS 모델이 있는 프로젝트만 — 요약의 모델별 분석 코드('/' 결합)로 판정
  const marsProjects = useMemo(
    () => projects.filter((p) => p.analysis_codes.some((codes) => codes.split('/').includes('MARS'))),
    [projects],
  );

  // 선택된 프로젝트의 본문 — 선택이 바뀌면 다시 받는다. 늦게 온 응답은 범위(useAsyncScope, 키 = 선택 id)가 버린다
  const selectedProjectId = value?.projectId && marsProjects.some((p) => p.id === value.projectId) ? value.projectId : null;
  const bodyScope = useAsyncScope(selectedProjectId);
  useEffect(() => {
    setProjectError(null);
    if (!selectedProjectId) { setSelectedProject(null); return; }
    const owns = bodyScope.claim();
    // 스토어의 읽기 전용 조회는 실패를 null 로 돌려준다 — 그것은 «본문 없음» 이 아니라 «받지 못함» 이다
    fetchProjectById(selectedProjectId).then((project) => {
      if (!owns()) return;
      setSelectedProject(project);
      if (!project) setProjectError('프로젝트 본문을 불러오지 못했습니다 — 다시 골라 주세요.');
    });
  }, [selectedProjectId, fetchProjectById, bodyScope]);
  // 선택된 프로젝트의 MARS 모델 목록
  const marsModels = useMemo<Model[]>(() => {
    if (!selectedProject?.data?.models) return [];
    return selectedProject.data.models.filter((m) => m.analysisCodes.includes('MARS'));
  }, [selectedProject]);

  // 모델 선택 후 BFF에서 시뮬레이션 이력 조회
  const fetchSimulationRuns = useCallback(async () => {
    const seq = ++runsFetchSeq.current;
    setSimulationRuns(null);
    setRunsError(null);
    if (!selectedProject || !value?.modelId) return;
    const projectId = selectedProject.id;
    // 무엇을 하다 실패했는지 그대로 말한다 — 이력 조회와 산출물 확인은 다른 일이다
    const failed = (what: string, err: unknown) => {
      if (seq !== runsFetchSeq.current) return;
      console.error(`[RestartSourceSelector] ${what}:`, err);
      setRunsError(`${what}: ${err instanceof Error ? err.message : String(err)}`);
    };
    let entries: SimulationEntry[];
    try {
      entries = await listSimulationHistoriesByProject(projectId);
    } catch (err) {
      // 실행 기록을 모를 때의 말은 기록 알림 정본 — 원문은 로그에만
      if (seq !== runsFetchSeq.current) return;
      console.error('[RestartSourceSelector] 실행 기록을 불러오지 못했습니다:', err);
      setRunsError(RUN_HISTORY_NOTICE.failed.text);
      return;
    }
    if (seq !== runsFetchSeq.current) return;
    try {
      // 원본이 될 수 있는 실행만 목록에 올린다(규칙은 restartSources 한 곳) — 산출물은 실행마다 확인한다
      const sources = await restartSources(entries, value.modelId, async (run, taskIndex) =>
        (await storageService.listResultFiles(projectId, run.id, taskIndex)).files.map((f) => f.fileName));
      if (seq !== runsFetchSeq.current) return;
      setSimulationRuns(sources);
    } catch (err) {
      failed('실행 산출물을 확인하지 못했습니다', err);
    }
  }, [selectedProject, value?.modelId]);

  useEffect(() => {
    fetchSimulationRuns();
  }, [fetchSimulationRuns]);

  // 프로젝트 선택
  const handleProjectChange = (projectId: string) => {
    const project = marsProjects.find((p) => p.id === projectId);
    if (!project) return;

    setSimulationRuns(null);
    onChange({
      projectId,
      projectName: project.name,
      modelId: '',
      modelName: '',
      restartNumber: -1,
    });
  };

  // 모델 선택
  const handleModelChange = (modelId: string) => {
    const model = marsModels.find((m) => m.id === modelId);
    if (!model || !value) return;

    onChange({
      ...value,
      modelId,
      modelName: model.name,
      simulationId: undefined,
      rstpltPath: undefined,
      restartNumber: -1,
    });
  };

  // 시뮬레이션 run 선택 — 목록에는 원본이 될 수 있는 실행만 있다. 참가자 순번은 산출물이 놓인 자리를 짚는 좌표다
  const handleRunChange = (runId: string) => {
    if (!value) return;
    const run = simulationRuns?.find((r) => r.id === runId);
    const taskIndex = run ? runTaskIndexOf(run, value.modelId) : null;
    if (!run || taskIndex === null) return;
    onChange({ ...value, simulationId: run.id, taskIndex, restartNumber: -1 });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={14} />
        <Typography variant="caption" color="text.secondary">
          프로젝트 목록 로딩 중...
        </Typography>
      </Box>
    );
  }

  if (marsProjects.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        MARS 모델이 있는 프로젝트가 없습니다. 먼저 NEW 해석으로 프로젝트를 생성해주세요.
      </Typography>
    );
  }

  const hasModel = !!value?.modelId;
  // 실행 목록을 알 수 없는 사유 — 프로젝트 본문이 없으면 실행 목록을 물을 수도 없다
  const sourceError = projectError ?? runsError;
  const runsPending = hasModel && simulationRuns === null && !sourceError;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Source Project */}
      <FormControl size="small" fullWidth>
        <InputLabel id={labelIds.project}>Source Project</InputLabel>
        <Select
          labelId={labelIds.project}
          value={value?.projectId ?? ''}
          label="Source Project"
          onChange={(e) => handleProjectChange(e.target.value)}
        >
          {marsProjects.map((project) => (
            <MenuItem key={project.id} value={project.id}>
              {project.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Source Model */}
      <FormControl size="small" fullWidth disabled={!value?.projectId} error={!!projectError}>
        <InputLabel id={labelIds.model}>Source Model</InputLabel>
        <Select
          labelId={labelIds.model}
          value={value?.modelId ?? ''}
          label="Source Model"
          onChange={(e) => handleModelChange(e.target.value)}
        >
          {marsModels.map((model) => (
            <MenuItem key={model.id} value={model.id}>
              {model.name}
            </MenuItem>
          ))}
        </Select>
        {projectError && !hasModel && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 0.5 }}>
            {projectError}
          </Typography>
        )}
      </FormControl>

      {/* Simulation Run */}
      {hasModel && (
        <FormControl size="small" fullWidth disabled={runsPending} error={!!sourceError}>
          <InputLabel id={labelIds.run}>Simulation Run</InputLabel>
          <Select
            labelId={labelIds.run}
            value={value?.simulationId ?? ''}
            label="Simulation Run"
            onChange={(e) => handleRunChange(e.target.value)}
            startAdornment={runsPending ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
          >
            {(simulationRuns ?? []).map((run) => {
              const dateLabel = formatDateTime(run.timestamp);
              return (
                <MenuItem key={run.id} value={run.id} sx={{ py: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flex: 1, mr: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {runEntryName(run)}
                      </Typography>
                      <Chip
                        label={runStatusLabel(run.status)}
                        color={runStatusTone(run.status)}
                        size="small"
                        sx={{ height: 18, fontSize: '0.7rem' }}
                      />
                      {isCoupledRun(run) && (
                        <Chip label={runModeLabel('cosim')} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.7rem' }} />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {run.duration ? `${dateLabel} | ${run.duration}` : dateLabel}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
          {selectedNotice && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, ml: 0.5, display: 'block' }} data-testid="restart-source-notice">
              {selectedNotice}
            </Typography>
          )}
          {sourceError && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 0.5 }}>
              {sourceError}
            </Typography>
          )}
          {!sourceError && simulationRuns?.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 0.5 }}>
              {restartSourceEmptyText()}
            </Typography>
          )}
        </FormControl>
      )}

    </Box>
  );
};

export default RestartSourceSelector;
